import React, {
  forwardRef,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Button,
  Modal as MuiModal,
  ModalProps as MuiModalProps,
  Stack,
} from '@mui/material';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalInner,
} from '@/components/@IntranetLibrary/Modal/styles';
import classNames from 'classnames';
import ArrowLeftIcon from '@/public/icons/arrow-left.svg';
import HeaderButtonBox, {
  HeaderButtonBoxProps,
} from '@/components/@IntranetLibrary/Modal/HeaderButtonBox';

type CustomModalSize = 'x-small' | 'small' | 'basic' | 'expand';

export const ModalPortalId = 'modal-portal-root';
export type ModalRef = {
  isOpen: boolean;
  open: (props?: any) => void;
  close: () => void;
};
export type ModalProps = PropsWithChildren<
  {
    title: string;
    size?: CustomModalSize;
    isFixedHeight?: boolean; // 모달 height 고정
    isBlockBackDropClose?: boolean; // 모달 backdrop 클릭 시 닫힘 막기
    isShowBackButton?: boolean;
    handleBack?: () => void;
    additionalButton?: ReactNode;
    availableAutoClose?: boolean;
    onOpen?: (props?: any) => void;
    handleClose?: () => void;
    footerContent?: ReactNode;
    overlayContent?: ReactNode;
    onClickCloseButton?: () => void;
    onClickExpandButton?: (isExpand: boolean) => void;
    isCloseButtonDisabled?: boolean;
    noBodyPadding?: boolean; // 모달 본문 패딩 제거 여부 (기본 false=기존 32px 24px)
  } & Pick<HeaderButtonBoxProps, 'isExpandable'> &
    Omit<MuiModalProps, 'open' | 'onClose' | 'ref'>
>;

const Modal = forwardRef<ModalRef, ModalProps>((_props, ref) => {
  const {
    children,
    title,
    size: _size = 'basic',
    isFixedHeight = false,
    isBlockBackDropClose = false,
    isExpandable = false,
    isShowBackButton = false,
    handleBack,
    additionalButton,
    availableAutoClose = true,
    onOpen,
    handleClose,
    footerContent,
    overlayContent,
    onClickCloseButton,
    onClickExpandButton,
    isCloseButtonDisabled = false,
    noBodyPadding = false,
    ...props
  } = _props;
  const [isOpen, setIsOpen] = useState(false);
  const [size, setSize] = useState<CustomModalSize>(_size);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  // size prop(_size) 변경을 내부 상태에 동기화한다.
  // (반응형 size 등 런타임에 prop 이 바뀌는 경우 반영 — 확장 토글 상태는 _size 가
  //  그대로면 재실행되지 않아 유지된다.)
  useEffect(() => {
    setSize(_size);
  }, [_size]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const target = document.getElementById(ModalPortalId);
      setPortalTarget(target || document.body);
    }
  }, []);

  const open = (props?: any) => {
    onOpen?.(props);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setSize(_size);
  };

  const onClose = () => {
    if (availableAutoClose) close();
    handleClose?.();
  };

  const handleClickExpandButton = () => {
    const isExpand = size === 'expand';
    setSize(isExpand ? _size : 'expand');
    onClickExpandButton?.(!isExpand);
  };

  useImperativeHandle(ref, () => ({
    isOpen,
    open,
    close,
  }));

  if (!portalTarget) return null;

  return createPortal(
    <MuiModal
      open={isOpen}
      onClose={(event, reason) => {
        if (isBlockBackDropClose && reason === 'backdropClick') return;
        onClose();
      }}
      {...props}
    >
      <ModalInner
        className={classNames('modal-inner', size, {
          fixedHeight: isFixedHeight,
        })}
      >
        <ModalHeader className={'modal-header'}>
          <h1 style={{ paddingLeft: isShowBackButton ? '4px' : '8px' }}>
            {isShowBackButton && (
              <Button className={'back'} onClick={handleBack}>
                <ArrowLeftIcon width={'24px'} height={'24px'} />
              </Button>
            )}
            {title}
          </h1>
          <HeaderButtonBox
            isExpandable={isExpandable}
            isExpand={size === 'expand'}
            handleClickExpandButton={handleClickExpandButton}
            handleClickCloseButton={() => {
              onClose();
              onClickCloseButton?.();
            }}
            isCloseButtonDisabled={isCloseButtonDisabled}
          >
            {additionalButton}
          </HeaderButtonBox>
        </ModalHeader>
        <Stack position="relative" flex={1} minHeight={0}>
          <ModalBody
            className={classNames('modal-body', {
              'no-padding': noBodyPadding,
            })}
          >
            {children}
          </ModalBody>
          {footerContent && (
            <ModalFooter className={'modal-footer'}>
              {footerContent}
            </ModalFooter>
          )}
          {overlayContent}
        </Stack>
      </ModalInner>
    </MuiModal>,
    portalTarget,
  );
});
Modal.displayName = 'Modal';

export default Modal;
